import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import {
  AHSeparator,
  AHText,
  Box,
  Circle,
  Dialog,
  Input,
  ListItem,
  useLayoutContext,
  useTheme,
} from "arkh";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { ListItemRightDateTime } from "./ListItemRightDateTime";
import { Platform, Switch, FlatList } from "react-native";

import { FormattedMessage } from "react-intl";
import { OrgButton } from "../OrgHome/OrgButton";
import { CalendarTeamEventEditComponentSelectCalendarPopover_storeCalendars$key } from "./__generated__/CalendarTeamEventEditComponentSelectCalendarPopover_storeCalendars.graphql";
import { CalendarTeamEventEditComponent_storeCalendars$key } from "./__generated__/CalendarTeamEventEditComponent_storeCalendars.graphql";

function CalendarTeamEventEditSelectCalendarPopover(props: {
  calendars: CalendarTeamEventEditComponentSelectCalendarPopover_storeCalendars$key;
  visible: boolean;
  onClose: () => void;
  selectedCalendarId: string;
  setSelectedCalendarId: (calendarId: string) => void;
}) {
  const intl_tokens = useIntlTokens();
  const calendars = useFragment(
    graphql`
      fragment CalendarTeamEventEditComponentSelectCalendarPopover_storeCalendars on StoreCalendar
      @relay(plural: true) {
        id
        title
        color
        isExternal
      }
    `,
    props.calendars
  );
  return (
    <Dialog
      visible={props.visible}
      onClose={props.onClose}
      minHeight={400}
      title={
        <FormattedMessage
          defaultMessage="Select Calendar"
          description="CalendarAdminComponent: Select Calendar Popover Title"
        />
      }
    >
      <FlatList
        data={calendars.filter((calendar) => !calendar.isExternal)}
        ItemSeparatorComponent={AHSeparator}
        renderItem={({ item }) => {
          return (
            <ListItem
              left={
                <Circle size="small" style={{ backgroundColor: item.color }} />
              }
              right={
                <ListItem.SingleSelectIndicator
                  selected={props.selectedCalendarId === item.id}
                />
              }
              onPress={() => props.setSelectedCalendarId(item.id)}
            >
              <ListItem.Title>{item.title}</ListItem.Title>
            </ListItem>
          );
        }}
      />
      <Box space="medium" shadow="strong" color="surface-primary">
        <OrgButton
          style={{ alignSelf: "stretch" }}
          title={intl_tokens.Done}
          backgroundColor="primary"
          tintColor="text-inverted"
          onPress={() => {
            props.onClose();
          }}
        />
      </Box>
    </Dialog>
  );
}

export function CalendarTeamEventEditComponent(props: {
  calendars: CalendarTeamEventEditComponent_storeCalendars$key;
  calendarId: string;
  setCalendarId: (calendarId: string) => void;
  isAllDay: boolean;
  setIsAllDay: (isAllDay: boolean) => void;
  startAt: DateTime;
  setStartAt: (startAt: DateTime) => void;
  endAt: DateTime;
  setEndAt: (endAt: DateTime) => void;
  title: string;
  setTitle: (title: string) => void;
  editable: boolean;
  timezone: string;
}) {
  const calendars = useFragment(
    graphql`
      fragment CalendarTeamEventEditComponent_storeCalendars on StoreCalendar
      @relay(plural: true) {
        id
        title
        color
        ...CalendarTeamEventEditComponentSelectCalendarPopover_storeCalendars
      }
    `,
    props.calendars
  );
  const selectedCalendar = useMemo(
    () => calendars.find((calendar) => calendar.id === props.calendarId),
    [calendars, props.calendarId]
  );
  const [calendarSelectOpen, setCalendarSelectOpen] = useState(false);
  const intl_tokens = useIntlTokens();

  const theme = useTheme();
  const layout = useLayoutContext();

  return (
    <>
      <Box space="medium">
        <Box
          space="xsmall"
          cornerRadius="large"
          color="surface-primary"
          borderColor="border-primary"
          style={{
            borderWidth: 1,
          }}
        >
          <Input
            editable={props.editable}
            disabled={!props.editable}
            value={props.title}
            onChangeText={props.editable ? props.setTitle : undefined}
            placeholder="Title"
            style={{
              borderWidth: 0,
              padding: layout.space.xsmall + layout.space.small,
            }}
          />
        </Box>
        <Box spaceTop="medium" />
        <ListItem
          disabled
          isFirst
          border="border-primary"
          right={
            <ListItemRightDateTime
              date={props.startAt}
              tz={props.timezone}
              setDate={(date) => {
                if (date) {
                  props.setStartAt(date);
                  if (date > props.endAt) {
                    props.setEndAt(date.plus({ hours: 1 }));
                  }
                }
              }}
              placeholder={intl_tokens.Add}
              isAllDay={props.isAllDay}
              disabled={!props.editable}
            />
          }
        >
          <ListItem.Title>{intl_tokens.Starts}</ListItem.Title>
        </ListItem>
        <AHSeparator />
        <ListItem
          disabled
          border="border-primary"
          right={
            <ListItemRightDateTime
              tz={props.timezone}
              date={props.endAt}
              setDate={(date) => {
                if (date) {
                  props.setEndAt(date);
                  if (date < props.startAt) {
                    props.setStartAt(date.minus({ hours: 1 }));
                  }
                }
              }}
              placeholder={intl_tokens.Add}
              isAllDay={props.isAllDay}
              disabled={!props.editable}
            />
          }
        >
          <ListItem.Title>{intl_tokens.Ends}</ListItem.Title>
        </ListItem>
        <AHSeparator />
        <ListItem
          isLast
          disabled
          border="border-primary"
          right={
            <Switch
              disabled={!props.editable}
              value={props.isAllDay}
              onValueChange={props.setIsAllDay}
              trackColor={{
                false: theme["surface-backgroundControl"],
                true: theme.primary,
              }}
              {...(Platform.OS === "web"
                ? { activeThumbColor: theme["surface-primary"] }
                : {})}
              thumbColor={theme["surface-primary"]}
            />
          }
        >
          <ListItem.Title>{intl_tokens.AllDay}</ListItem.Title>
        </ListItem>
        <Box spaceTop="medium" />
        <ListItem
          isFirst
          isLast
          disabled
          border="border-primary"
          right={
            <ListItem.EditableValueContainer
              editable={props.editable}
              onPress={() => setCalendarSelectOpen(true)}
            >
              <Circle
                size="badge"
                style={{
                  backgroundColor: selectedCalendar?.color,
                }}
              />
              <AHText spaceLeft="xsmall" type="subtitle" numberOfLines={1}>
                {selectedCalendar?.title ?? "-"}
              </AHText>
            </ListItem.EditableValueContainer>
          }
        >
          <ListItem.Title>{intl_tokens.Calendar}</ListItem.Title>
        </ListItem>
      </Box>
      {props.editable ? (
        <CalendarTeamEventEditSelectCalendarPopover
          visible={calendarSelectOpen}
          onClose={() => setCalendarSelectOpen(false)}
          calendars={calendars}
          selectedCalendarId={props.calendarId}
          setSelectedCalendarId={props.setCalendarId}
        />
      ) : null}
    </>
  );
}
